import React, { useCallback, useEffect, useState, useRef } from 'react';
import { loadStripe, Stripe, StripeElements, StripeCardElement } from '@stripe/stripe-js';
import { Button } from './ui/Button';
import { Loader2, AlertTriangle, Lock, CheckCircle, ArrowLeft, ShieldCheck } from 'lucide-react';
// Replace with your real Stripe publishable key
const STRIPE_PK = 'pk_live_51TJhjqKCGBLfkXSeMsfRv0pSwFrEG58uWUaMFKh5ZlooNCBrf7yJkH4xuH16rDNR15l3owDfsB8KqR5ZaHdUoCqo00XrmtP3FC';
interface StripePaymentFormProps {
  onPaymentSuccess: (paymentMethodId: string) => void;
  onBack: () => void;
  isProcessing: boolean;
  setIsProcessing: (v: boolean) => void;
  customerName: string;
  customerEmail: string;
  selectedPrice: string;
}
export function StripePaymentForm({
  onPaymentSuccess,
  onBack,
  isProcessing,
  setIsProcessing,
  customerName,
  customerEmail,
  selectedPrice
}: StripePaymentFormProps) {
  const cardRef = useRef<HTMLDivElement>(null);
  const stripeRef = useRef<Stripe | null>(null);
  const elementsRef = useRef<StripeElements | null>(null);
  const cardElementRef = useRef<StripeCardElement | null>(null);
  const [cardError, setCardError] = useState<string | null>(null);
  const [cardComplete, setCardComplete] = useState(false);
  const [stripeReady, setStripeReady] = useState(false);
  const [loadError, setLoadError] = useState(false);
  useEffect(() => {
    let mounted = true;
    async function initStripe() {
      try {
        const stripe = await loadStripe(STRIPE_PK);
        if (!mounted || !stripe) {
          if (mounted) setLoadError(true);
          return;
        }
        stripeRef.current = stripe;
        const elements = stripe.elements();
        elementsRef.current = elements;
        const card = elements.create('card', {
          style: {
            base: {
              fontSize: '16px',
              color: '#3D3229',
              fontFamily: 'Inter, system-ui, sans-serif',
              '::placeholder': {
                color: '#9C9488'
              },
              iconColor: '#7A9E7E'
            },
            invalid: {
              color: '#DC4B4B',
              iconColor: '#DC4B4B'
            }
          },
          hidePostalCode: false
        });
        cardElementRef.current = card;
        card.on('change', event => {
          if (!mounted) return;
          setCardComplete(event.complete);
          setCardError(event.error ? event.error.message : null);
        });
        if (cardRef.current) {
          card.mount(cardRef.current);
          if (mounted) setStripeReady(true);
        }
      } catch (err) {
        if (mounted) setLoadError(true);
      }
    }
    initStripe();
    return () => {
      mounted = false;
      if (cardElementRef.current) {
        try {
          cardElementRef.current.unmount();
        } catch {}
      }
    };
  }, []);
  const handleSubmit = useCallback(async () => {
    const stripe = stripeRef.current;
    const card = cardElementRef.current;
    if (!stripe || !card) return;
    setIsProcessing(true);
    setCardError(null);
    const {
      error,
      paymentMethod
    } = await stripe.createPaymentMethod({
      type: 'card',
      card,
      billing_details: {
        name: customerName,
        email: customerEmail
      }
    });
    if (error) {
      setCardError(error.message || 'Error al procesar el pago');
      setIsProcessing(false);
      return;
    }
    if (paymentMethod) {
      onPaymentSuccess(paymentMethod.id);
    }
    setIsProcessing(false);
  }, [customerName, customerEmail, onPaymentSuccess, setIsProcessing]);
  return <div data-id="element-1115">
      <div className="bg-sand rounded-2xl p-5 mb-6" data-id="element-1116">
        <p className="text-sm font-semibold text-stone uppercase tracking-widest mb-4 flex items-center gap-2" data-id="element-1117">
          <ShieldCheck size={16} className="text-sage" data-id="element-1118" />
          Pago seguro
        </p>

        <div className="bg-white rounded-xl p-4 border-2 border-mist focus-within:border-sage focus-within:ring-4 focus-within:ring-sage/10 transition-all relative" data-id="element-1119">
          {!stripeReady && !loadError ? <div className="absolute inset-0 flex items-center justify-center bg-white/80 z-10 rounded-xl" data-id="element-1120">
              <span className="flex items-center gap-2 text-sm text-stone" data-id="element-1121">
                <Loader2 size={16} className="animate-spin" data-id="element-1122" />
                Conectando con Stripe...
              </span>
            </div> : null}

          <div ref={cardRef} className={`min-h-[40px] ${!stripeReady && !loadError ? 'opacity-0 h-0 overflow-hidden' : ''}`} data-id="element-1123" />
        </div>

        {cardError && <p className="text-xs text-error mt-2 flex items-center gap-1" data-id="element-1124">
            <AlertTriangle size={14} data-id="element-1125" /> {cardError}
          </p>}

        <p className="text-xs text-stone mt-3 flex items-center gap-2" data-id="element-1126">
          <Lock size={14} data-id="element-1127" /> Encriptado con SSL · Procesado por Stripe
        </p>
      </div>

      <div className="flex gap-3" data-id="element-1128">
        <Button variant="outline" className="flex-1 flex items-center justify-center gap-2" onClick={onBack} disabled={isProcessing} data-id="element-1129">
          <ArrowLeft size={16} data-id="element-1130" /> Atrás
        </Button>
        <Button variant="sage" className="flex-[2]" disabled={isProcessing || !stripeReady || !cardComplete || loadError} onClick={handleSubmit} data-id="element-1131">
          {isProcessing ? <span className="flex items-center gap-2" data-id="element-1132">
              <Loader2 size={16} className="animate-spin" data-id="element-1133" />
              Procesando...
            </span> : <span className="flex items-center gap-2" data-id="element-1134">
              <CheckCircle size={16} data-id="element-1135" /> Pagar {selectedPrice}
            </span>}
        </Button>
      </div>

      <p className="text-xs text-stone text-center mt-4" data-id="element-1136">
        Al confirmar aceptas nuestra política de cancelación: reembolso completo
        hasta 24h antes de la clase.
      </p>
    </div>;
}